import type * as bedrockAgent from '@aws-sdk/client-bedrock-agent-runtime'

import * as common from '../common'

/**
 * Shape of an S3-backed file reference accepted by Bedrock agent
 * invocations. Defined here so both {@link Message.File} (per-message
 * uploads) and {@link Configuration.Context.File} (per-conversation
 * context files) can produce structurally identical references without
 * either having to know about the other.
 */
export interface S3Reference extends bedrockAgent.InputFile {
  name: string
  source: {
    s3Location: { uri: string }
    sourceType: 'S3'
  }
  useCase: 'CHAT'
}

/**
 * Strips `userId` from a key-generation context so that S3 keys match
 * the paths produced by upload/download resolvers (which do not include
 * a `U/{userId}/` segment).
 */
export function stripUserId(
  context: common.fileManagement.Key.Parts.Context,
): common.fileManagement.Key.Parts.Context {
  const { userId: _, ...rest } = context as Record<string, unknown>
  // NOTE: Cast is safe because Context is a union of admin contexts
  // (ForTenant, ForZone, etc.) and we only removed userId — the
  // remaining properties still satisfy at least ForTenant.
  return rest as common.fileManagement.Key.Parts.Context
}

/**
 * Builds a single Bedrock-compatible {@link S3Reference} from a file name
 * and a precomputed qualifier. Callers (per-message vs. per-context) own
 * their qualifier strategy and hand the resulting string to this helper —
 * keeping qualifier semantics out of the URI-building primitive.
 */
export function buildS3Reference(params: {
  context: common.fileManagement.Key.Parts.Context
  fileName: string
  bucketName: string
  qualifier: string
}): S3Reference {
  const { context, fileName, bucketName, qualifier } = params
  const fullKey = common.fileManagement.Key.from({
    context: stripUserId(context),
    id: fileName,
    purpose: common.fileManagement.Purpose.FileManagement,
    qualifier,
  })
  return {
    name: fileName,
    source: {
      s3Location: { uri: `s3://${bucketName}/${fullKey}` },
      sourceType: 'S3' as const,
    },
    useCase: 'CHAT' as const,
  }
}
